'use client';

import { c as _c } from "react/compiler-runtime";
import { createContext, type Dispatch, type PropsWithChildren, useContext, useEffect, useReducer, useRef, useState, useSyncExternalStore } from 'react';
import { type getInitialUserFromCookie } from '../helpers/parseJwt';
import { buildInitialUserState, initialUserState, type UserAction, type UserState } from './context';
import { userReducer } from './reducers/userReducer';
type UserContextType = {
  state: UserState;
  dispatch: Dispatch<UserAction>;
  /** The state the provider started from, which is what the server rendered. */
  initial: UserState;
};
const UserContext = createContext<UserContextType>({
  state: initialUserState,
  dispatch: () => {},
  initial: initialUserState
});

// The user also goes out in slices, each its own context holding a primitive,
// so a component that only needs the id or whether someone is signed in
// rerenders when that changes, not when the balance, the bio or the avatar
// does. The user stays React state (not a store): Main applies the session
// from the cookie in a transition, and a store update cannot be one.
const UserIdContext = createContext<number>(initialUserState.user.id);
const IsAuthenticatedContext = createContext<boolean | null>(initialUserState.user.isAuthenticated);
const UsernameContext = createContext<string>(initialUserState.user.username);
const IsPrivateContext = createContext<boolean>(initialUserState.user.isPrivate);
// The dispatch, the starting state and a reader of the current user for
// event handlers. None of them ever changes.
const UserStaticContext = createContext<{
  dispatch: Dispatch<UserAction>;
  initial: UserState;
  getUser: () => UserState['user'];
}>({
  dispatch: () => {},
  initial: initialUserState,
  getUser: () => initialUserState.user
});

// useSyncExternalStore snapshots: React reads the server one (false) while it
// hydrates and the client one (true) on every render after. React Native
// never hydrates, so there it is always true.
const subscribeToNothing = () => () => {};
const clientSnapshot = () => true;
const serverSnapshot = () => false;
const useHydrated = () => {
  return useSyncExternalStore(subscribeToNothing, clientSnapshot, serverSnapshot);
};

/**
 * The user state, except while hydrating, when it is the state the provider
 * started from (the one the server rendered). Main applies the session from
 * the cookie right after mount, and a Suspense boundary that hydrates after
 * that would otherwise render signed in against signed-out server HTML:
 * React #418 on every signed-in page (the header bell, the home sidebar, the
 * composer's avatar). The real state follows on the very next render.
 */
export const useUserState = () => {
  const $ = _c(3);
  const context = useContext(UserContext);
  const hydrated = useHydrated();
  let t0;
  if ($[0] !== context || $[1] !== hydrated) {
    t0 = hydrated ? context : {
      ...context,
      state: context.initial
    };
    $[0] = context;
    $[1] = hydrated;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};

// The slice hooks below follow the same rule as useUserState: while
// hydrating they answer from the starting state, or hydration mismatches
// come back.

/** The signed-in user's id (0 when nobody is). */
export const useCurrentUserId = () => {
  const id = useContext(UserIdContext);
  const {
    initial
  } = useContext(UserStaticContext);
  return useHydrated() ? id : initial.user.id;
};

/** Whether someone is signed in; null until that is known. */
export const useIsAuthenticated = () => {
  const isAuthenticated = useContext(IsAuthenticatedContext);
  const {
    initial
  } = useContext(UserStaticContext);
  return useHydrated() ? isAuthenticated : initial.user.isAuthenticated;
};

/** Who is signed in: the fields most components need, and nothing else. */
export const useUserIdentity = () => {
  const $ = _c(10);
  const id = useContext(UserIdContext);
  const isAuthenticated = useContext(IsAuthenticatedContext);
  const username = useContext(UsernameContext);
  const isPrivate = useContext(IsPrivateContext);
  const {
    initial
  } = useContext(UserStaticContext);
  if (!useHydrated()) {
    const {
      user
    } = initial;
    let t0;
    if ($[0] !== user.id || $[1] !== user.isAuthenticated || $[2] !== user.isPrivate || $[3] !== user.username) {
      t0 = {
        id: user.id,
        isAuthenticated: user.isAuthenticated,
        username: user.username,
        isPrivate: user.isPrivate
      };
      $[0] = user.id;
      $[1] = user.isAuthenticated;
      $[2] = user.isPrivate;
      $[3] = user.username;
      $[4] = t0;
    } else {
      t0 = $[4];
    }
    return t0;
  }
  let t0;
  if ($[5] !== id || $[6] !== isAuthenticated || $[7] !== isPrivate || $[8] !== username) {
    t0 = {
      id,
      isAuthenticated,
      username,
      isPrivate
    };
    $[5] = id;
    $[6] = isAuthenticated;
    $[7] = isPrivate;
    $[8] = username;
    $[9] = t0;
  } else {
    t0 = $[9];
  }
  return t0;
};

/** The user reducer's dispatch. Never changes. */
export const useUserDispatch = () => {
  return useContext(UserStaticContext).dispatch;
};

/**
 * Reads the current user when called, for event handlers and async work that
 * need fields beyond the slices. Never rerenders the caller; do not call it
 * during render (it is current as of the last commit).
 */
export const useGetCurrentUser = () => {
  return useContext(UserStaticContext).getUser;
};
type Props = PropsWithChildren<{
  // Server-injected initial payload (parsed from the ft-client cookie via
  // next/headers). When provided, the first render uses this instead of the
  // module-level `initialUserState` so SSR and client agree.
  initialUser?: ReturnType<typeof getInitialUserFromCookie>;
}>;
export const UserContextProvider = t0 => {
  const $ = _c(28);
  const {
    children,
    initialUser
  } = t0;
  let t1;
  if ($[0] !== initialUser) {
    t1 = initialUser !== undefined ? buildInitialUserState(initialUser) : initialUserState;
    $[0] = initialUser;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const initial = t1;
  const [state, dispatch] = useReducer(userReducer, initial);
  const userRef = useRef(state.user);
  let t2;
  if ($[2] !== initial) {
    t2 = () => ({
      dispatch,
      initial,
      getUser: () => userRef.current
    });
    $[2] = initial;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [staticValue] = useState(t2);
  const {
    user
  } = state;
  let t3;
  let t4;
  if ($[4] !== user) {
    t3 = () => {
      userRef.current = user;
    };
    t4 = [user];
    $[4] = user;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] !== state || $[8] !== staticValue.initial) {
    t5 = {
      state,
      dispatch,
      initial: staticValue.initial
    };
    $[7] = state;
    $[8] = staticValue.initial;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== children || $[11] !== user.isPrivate) {
    t6 = <IsPrivateContext.Provider value={user.isPrivate}>{children}</IsPrivateContext.Provider>;
    $[10] = children;
    $[11] = user.isPrivate;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t6 || $[14] !== user.username) {
    t7 = <UsernameContext.Provider value={user.username}>{t6}</UsernameContext.Provider>;
    $[13] = t6;
    $[14] = user.username;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t7 || $[17] !== user.isAuthenticated) {
    t8 = <IsAuthenticatedContext.Provider value={user.isAuthenticated}>{t7}</IsAuthenticatedContext.Provider>;
    $[16] = t7;
    $[17] = user.isAuthenticated;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t8 || $[20] !== user.id) {
    t9 = <UserIdContext.Provider value={user.id}>{t8}</UserIdContext.Provider>;
    $[19] = t8;
    $[20] = user.id;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t5 || $[23] !== t9) {
    t10 = <UserContext.Provider value={t5}>{t9}</UserContext.Provider>;
    $[22] = t5;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== staticValue || $[26] !== t10) {
    t11 = <UserStaticContext.Provider value={staticValue}>{t10}</UserStaticContext.Provider>;
    $[25] = staticValue;
    $[26] = t10;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  return t11;
};